import { CircularProgress, Divider, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { formatTimestampWithOffset } from "src/utils/formatTimestampWithOffset";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LabellingCostDailyEntry,
  useGetLabellingCostDailyQuery,
} from "src/store/api/generatedApi";
import { SummaryChip } from "components/scrapEyeDataDashboard/trainableAndCoverage/SummaryChip";

const TRAILING_AVERAGE_DAYS = 7;
const MONTHLY_ESTIMATE_GROUP_BY = "session_date";

type TenantDescriptor = Readonly<{
  tenantName: string;
  tenantLongName: string;
}>;

type CombinedCostSummaryProps = Readonly<{
  tenants: TenantDescriptor[];
  start: string;
  end: string;
  groupBy: "session_date" | "footage_date";
}>;

type TenantCostState = Readonly<{
  entries: LabellingCostDailyEntry[];
  flatRateCostGbp: number;
  isLoading: boolean;
}>;

type OnTenantCostLoaded = (tenantName: string, state: TenantCostState) => void;

// The flat-rate salary spans every tenant and is returned identically on each tenant's
// all-tenants response, so it is counted once rather than summed across tenants.
const firstFlatRateCostGbp = (
  tenants: TenantDescriptor[],
  stateByTenant: Record<string, TenantCostState>
): number => {
  const resolved = tenants
    .map((tenant) => stateByTenant[tenant.tenantName])
    .find((state) => state !== undefined);
  return resolved?.flatRateCostGbp ?? 0;
};

const TenantCostDataLoader = ({
  tenantName,
  start,
  end,
  groupBy,
  onLoaded,
}: Readonly<{
  tenantName: string;
  start: string;
  end: string;
  groupBy: "session_date" | "footage_date";
  onLoaded: OnTenantCostLoaded;
}>) => {
  const { data, isLoading } = useGetLabellingCostDailyQuery(
    { tenantName, start, end, groupBy, costScope: "all_tenants" },
    { skip: !start || !end }
  );
  const entries = data?.entries;
  const flatRateCostGbp = data?.flat_rate_cost_gbp;

  useEffect(() => {
    onLoaded(tenantName, {
      entries: entries ?? [],
      flatRateCostGbp: flatRateCostGbp ?? 0,
      isLoading,
    });
  }, [tenantName, entries, flatRateCostGbp, isLoading, onLoaded]);

  return null;
};

export const CombinedCostSummary = ({
  tenants,
  start,
  end,
  groupBy,
}: CombinedCostSummaryProps) => {
  const { t } = useTenantTranslation();
  const formatNumber0dp = useFormatNumber()(0);
  const formatNumber2dp = useFormatNumber()(2);
  const [stateByTenant, setStateByTenant] = useState<
    Record<string, TenantCostState>
  >({});

  const handleTenantCostLoaded: OnTenantCostLoaded = useCallback(
    (tenantName, state) => {
      setStateByTenant((previous) => {
        const existing = previous[tenantName];
        if (
          existing?.entries === state.entries &&
          existing.flatRateCostGbp === state.flatRateCostGbp &&
          existing.isLoading === state.isLoading
        ) {
          return previous;
        }
        return { ...previous, [tenantName]: state };
      });
    },
    []
  );

  const monthlyEstimateStart = useMemo(
    () => formatTimestampWithOffset(dayjs().startOf("month")),
    []
  );
  const monthlyEstimateEnd = useMemo(
    () => formatTimestampWithOffset(dayjs()),
    []
  );

  const [monthlyEstimateStateByTenant, setMonthlyEstimateStateByTenant] =
    useState<Record<string, TenantCostState>>({});

  const handleMonthlyEstimateLoaded: OnTenantCostLoaded = useCallback(
    (tenantName, state) => {
      setMonthlyEstimateStateByTenant((previous) => {
        const existing = previous[tenantName];
        if (
          existing?.entries === state.entries &&
          existing.flatRateCostGbp === state.flatRateCostGbp &&
          existing.isLoading === state.isLoading
        ) {
          return previous;
        }
        return { ...previous, [tenantName]: state };
      });
    },
    []
  );

  const allResolved = tenants.every((tenant) => {
    const state = stateByTenant[tenant.tenantName];
    return state !== undefined && !state.isLoading;
  });

  const allMonthlyEstimateResolved = tenants.every((tenant) => {
    const state = monthlyEstimateStateByTenant[tenant.tenantName];
    return state !== undefined && !state.isLoading;
  });

  const allEntries = useMemo(
    () =>
      tenants.flatMap(
        (tenant) => stateByTenant[tenant.tenantName]?.entries ?? []
      ),
    [tenants, stateByTenant]
  );

  const monthlyEstimateEntries = useMemo(
    () =>
      tenants.flatMap(
        (tenant) =>
          monthlyEstimateStateByTenant[tenant.tenantName]?.entries ?? []
      ),
    [tenants, monthlyEstimateStateByTenant]
  );

  const flatRateCost = useMemo(
    () => firstFlatRateCostGbp(tenants, stateByTenant),
    [tenants, stateByTenant]
  );

  const totals = useMemo(() => {
    const {
      labellingCost,
      reviewCost,
      labellingFootageHours,
      reviewFootageHours,
    } = allEntries.reduce(
      (accumulator, entry) => ({
        labellingCost: accumulator.labellingCost + entry.labelling_cost_gbp,
        reviewCost: accumulator.reviewCost + entry.review_cost_gbp,
        labellingFootageHours:
          accumulator.labellingFootageHours +
          entry.labelling_footage_seconds / 3600,
        reviewFootageHours:
          accumulator.reviewFootageHours + entry.review_footage_seconds / 3600,
      }),
      {
        labellingCost: 0,
        reviewCost: 0,
        labellingFootageHours: 0,
        reviewFootageHours: 0,
      }
    );
    return {
      labellingCost,
      reviewCost,
      totalCost: labellingCost + reviewCost + flatRateCost,
      labellingCostPerFootageHour:
        labellingFootageHours > 0
          ? labellingCost / labellingFootageHours
          : null,
      reviewCostPerFootageHour:
        reviewFootageHours > 0 ? reviewCost / reviewFootageHours : null,
    };
  }, [allEntries, flatRateCost]);

  const monthlyFlatRateCost = useMemo(
    () => firstFlatRateCostGbp(tenants, monthlyEstimateStateByTenant),
    [tenants, monthlyEstimateStateByTenant]
  );

  const monthlyPrediction = useMemo(() => {
    if (monthlyEstimateEntries.length === 0) return null;

    const today = dayjs();

    const costByDate = monthlyEstimateEntries.reduce<
      Map<string, { labelling: number; review: number }>
    >((accumulator, entry) => {
      const existing = accumulator.get(entry.date) ?? {
        labelling: 0,
        review: 0,
      };
      return accumulator.set(entry.date, {
        labelling: existing.labelling + entry.labelling_cost_gbp,
        review: existing.review + entry.review_cost_gbp,
      });
    }, new Map());

    const sortedDates = [...costByDate.keys()].sort();
    const trailingDates = sortedDates.slice(-TRAILING_AVERAGE_DAYS);
    const trailingDayCount = trailingDates.length;

    const actualLabelling = [...costByDate.values()].reduce(
      (sum, costs) => sum + costs.labelling,
      0
    );
    const actualReview = [...costByDate.values()].reduce(
      (sum, costs) => sum + costs.review,
      0
    );

    const trailingLabelling = trailingDates.reduce(
      (sum, date) => sum + (costByDate.get(date)?.labelling ?? 0),
      0
    );
    const trailingReview = trailingDates.reduce(
      (sum, date) => sum + (costByDate.get(date)?.review ?? 0),
      0
    );

    const averageDailyLabelling = trailingLabelling / trailingDayCount;
    const averageDailyReview = trailingReview / trailingDayCount;
    const remainingDays = today.daysInMonth() - today.date();

    return {
      monthName: today.format("MMMM"),
      estimatedLabellingCost:
        actualLabelling + remainingDays * averageDailyLabelling,
      estimatedReviewCost: actualReview + remainingDays * averageDailyReview,
      estimatedTotalCost:
        actualLabelling +
        actualReview +
        remainingDays * (averageDailyLabelling + averageDailyReview) +
        monthlyFlatRateCost,
      averageDailyCost: averageDailyLabelling + averageDailyReview,
      trailingDayCount,
    };
  }, [monthlyEstimateEntries, monthlyFlatRateCost]);

  return (
    <>
      {tenants.map((tenant) => (
        <TenantCostDataLoader
          key={tenant.tenantName}
          tenantName={tenant.tenantName}
          start={start}
          end={end}
          groupBy={groupBy}
          onLoaded={handleTenantCostLoaded}
        />
      ))}
      {tenants.map((tenant) => (
        <TenantCostDataLoader
          key={`monthly-estimate-${tenant.tenantName}`}
          tenantName={tenant.tenantName}
          start={monthlyEstimateStart}
          end={monthlyEstimateEnd}
          groupBy={MONTHLY_ESTIMATE_GROUP_BY}
          onLoaded={handleMonthlyEstimateLoaded}
        />
      ))}
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={4} alignItems="center">
          {!allResolved && <CircularProgress size={16} />}
          <SummaryChip
            label={t("totalCost")}
            value={`£${formatNumber0dp(totals.totalCost)}`}
          />
          <SummaryChip
            label={t("labellingCost")}
            value={`£${formatNumber0dp(totals.labellingCost)}`}
          />
          <SummaryChip
            label={t("reviewCost")}
            value={`£${formatNumber0dp(totals.reviewCost)}`}
          />
          <SummaryChip
            label={t("labellingCostPerFootageHour")}
            value={
              totals.labellingCostPerFootageHour != null
                ? `£${formatNumber2dp(totals.labellingCostPerFootageHour)}/h`
                : "\u2014"
            }
          />
          <SummaryChip
            label={t("reviewCostPerFootageHour")}
            value={
              totals.reviewCostPerFootageHour != null
                ? `£${formatNumber2dp(totals.reviewCostPerFootageHour)}/h`
                : "\u2014"
            }
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("selectedPeriod")}
        </Typography>
      </Stack>
      {allMonthlyEstimateResolved && monthlyPrediction != null && (
        <>
          <Divider />
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("monthlyEstimate", { month: monthlyPrediction.monthName })}
            </Typography>
            <Stack direction="row" spacing={4} alignItems="center">
              <SummaryChip
                label={t("estimatedTotalCost")}
                value={`£${formatNumber0dp(monthlyPrediction.estimatedTotalCost)}`}
              />
              <SummaryChip
                label={t("estimatedLabellingCost")}
                value={`£${formatNumber0dp(monthlyPrediction.estimatedLabellingCost)}`}
              />
              <SummaryChip
                label={t("estimatedReviewCost")}
                value={`£${formatNumber0dp(monthlyPrediction.estimatedReviewCost)}`}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("basedOnDailyAverage", {
                amount: formatNumber0dp(monthlyPrediction.averageDailyCost),
                days: String(monthlyPrediction.trailingDayCount),
              })}
            </Typography>
          </Stack>
        </>
      )}
    </>
  );
};
